'use client';

import { c as _c } from "react/compiler-runtime";
import type { CardAISessionSummary } from '@shared/all/domains/electron';
import type { Factiii } from '@shared/all/domains/factiiis';
import { ModerationReportType } from '@shared/all/domains/moderation';
import { LayoutType, SpaceMemberRoles, Types } from '@shared/all/domains/spaces';
import { type Upload } from '@shared/all/domains/uploads';
import { boardTerminalKey } from '@shared/all/helpers/board-agent-core/boardTerminal';
import { buildChatLink } from '@shared/all/helpers/chatLink';
import AppWidgetContext from '@shared/utils/context/AppWidgetContext';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { copyToClipboard } from '@shared/utils/helpers/clipboard';
import { DEMO_SLUG } from '@shared/utils/helpers/demoBoardData';
import { humanizeSetupError } from '@shared/utils/helpers/setupError';
import toast from '@shared/utils/helpers/toast';
import useSpace from '@shared/utils/hooks/useSpace';
import useUser from '@shared/utils/hooks/useUser';
import { type InfiniteData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertCircle, Apple, Cpu, Download, ExternalLink, History, Laptop, LayoutGrid, List, MessageCircle, MonitorCog, MoreVertical, Server, Sparkles, Tags, TerminalSquare } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';
import { focusTerminalWindow } from '../../helpers/terminalWindow';
import useClickOutside from '../../hooks/useClickOutside';
import { useDetachedTerminal } from '../../hooks/useDetachedTerminal';
import { useHeaderHeight } from '../../hooks/useHeaderHeight';
import useIsElectron from '../../hooks/useIsElectron';
import { useRunner } from '../../hooks/useRunnerContext';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import PageBanner, { BannerAction, BannerChip, BannerMenu, BannerStat } from '../common/PageBanner';
import PinnedFactiiis from '../common/PinnedFactiiis';
import FactiiisList from '../factiiis/FactiiisList';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import BanStatsButton from './BanStats';
import EmbedSpaceModal from './EmbedSpaceModal';
import HeaderBoards from './HeaderBoards';
import InviteMembersModal from './InviteMembersModal';
import RemovalStatsButton from './RemovalStats';
import SpaceDropdown from './SpaceDropdown';
import SpaceEdit from './SpaceEdit';
import SpaceMembersList from './SpaceMembersList';
import TrelloImportModal from './TrelloImportModal';
const BoardAIModal = dynamic(() => import('../pages/space/BoardAIModal'), {
  ssr: false
});
const DeployButton = dynamic(() => import('../pages/space/DeployButton'), {
  ssr: false
});
const SkillsButton = dynamic(() => import('../pages/space/SkillsButton'), {
  ssr: false
});
const CardAISessionModal = dynamic(() => import('../pages/space/CardAIModal'), {
  ssr: false
});
const BareCardSessionModal = dynamic(() => import('../pages/space/BareCardModal'), {
  ssr: false
});
const RunnerSelectorModal = dynamic(() => import('../pages/space/RunnerSelectorModal'), {
  ssr: false
});

// Dynamic also breaks the import cycle: the demo renders this header
const DevelopmentDemo = dynamic(() => import('../onboarding/DevelopmentDemo'), {
  ssr: false
});
type Props = {
  uploads?: Upload[] | undefined;
  showSubscribeModalHandler?: () => void;
  space: RouterOutputs['spaces']['get'];
  isHomePage?: boolean;
  isEmbed: boolean;
  // Effective board layout including the client-side `?view=board` override;
  // falls back to the space's stored layout when not provided.
  isBoardLayout?: boolean;
  onHistoryClick?: () => void;
  // When true, the header collapses its top margin because another element
  // (like the invite banner) is already clearing the fixed top bar above it.
  hasBanner?: boolean;
};
const Header = props => {
  const $ = _c(120);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["users", "spaceFilters"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = currentUser.isAuthenticated === true;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1,
      staleTime: Infinity
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: t3
  } = useQuery(t2);
  let t4;
  if ($[3] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const spaceFilterIds = t4;
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const {
    newModerationReport
  } = useContext(AppWidgetContext);
  const {
    isOwner,
    joinSpaceUpdate,
    leaveSpaceUpdate
  } = useSpace();
  const [factiiiModalMode, setFactiiiModalMode] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showMembersListModal, setShowMembersListModal] = useState(false);
  const [profileOptionsDropdownOpen, setProfileOptionsDropdownOpen] = useState(false);
  const [inviteMembersModalOpen, setInviteMembersModalOpen] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const [embedSpaceModalOpen, setEmbedSpaceModalOpen] = useState(false);
  const [trelloImportModalOpen, setTrelloImportModalOpen] = useState(false);
  const [runnerSelectorOpen, setRunnerSelectorOpen] = useState(false);
  const [aiSessionsOpen, setAiSessionsOpen] = useState(false);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [aiSessions, setAiSessions] = useState(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      postId: null,
      forceBare: false
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [aiModal, setAiModal] = useState(t6);
  const [boardAIOpen, setBoardAIOpen] = useState(false);
  const [terminalWindowOpen, setTerminalWindowOpen] = useState(false);
  const detached = useDetachedTerminal();
  const [boardTourOpen, setBoardTourOpen] = useState(false);
  const isElectron = useIsElectron();
  const headerClearance = useHeaderHeight();
  const {
    runners,
    selectedRunner,
    agent: boardAgent,
    config: runnerConfig
  } = useRunner();
  const runnerSetup = runnerConfig.setup;
  const runnerUnhealthy = runnerSetup.phase === "error";
  let t7;
  if ($[7] !== selectedRunner) {
    t7 = r => r.socketId === selectedRunner;
    $[7] = selectedRunner;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const activeRunner = runners.find(t7);
  const queryClient = useQueryClient();
  const dropdownRef = useRef(null);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setProfileOptionsDropdownOpen(false);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  useClickOutside(dropdownRef, t8);
  let t9;
  if ($[10] !== props.space.id || $[11] !== spaceFilterIds) {
    t9 = spaceFilterIds.includes(props.space.id);
    $[10] = props.space.id;
    $[11] = spaceFilterIds;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const filtered = t9;
  const spaceMembership = props.space.membership;
  let t10;
  if ($[13] !== spaceMembership?.roles) {
    t10 = spaceMembership?.roles.includes(SpaceMemberRoles.INVITED) || false;
    $[13] = spaceMembership?.roles;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const isInvitedRole = t10;
  let t11;
  if ($[15] !== isInvitedRole || $[16] !== props.space.types || $[17] !== spaceMembership) {
    t11 = !spaceMembership && !props.space.types.includes(Types.INVITE) || isInvitedRole;
    $[15] = isInvitedRole;
    $[16] = props.space.types;
    $[17] = spaceMembership;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const canJoin = t11;
  let t12;
  if ($[19] !== isOwner || $[20] !== props.space || $[21] !== spaceMembership) {
    t12 = spaceMembership && spaceMembership.roles.includes(SpaceMemberRoles.MEMBER) && !isOwner(props.space);
    $[19] = isOwner;
    $[20] = props.space;
    $[21] = spaceMembership;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const canLeave = t12;
  const spaceSlug = props.space.spaceSlug;
  const defaultsToBoard = props.space?.config?.defaultLayout === LayoutType.Board;
  const isBoardLayout = props.isBoardLayout ?? defaultsToBoard;
  const canToggleView = !props.isHomePage;
  let t13;
  let t14;
  if ($[23] !== boardAgent) {
    t13 = () => {
      if (!boardAgent) {
        return;
      }
      let ignore = false;
      boardAgent.cardSessions().then(sessions => {
        if (!ignore) {
          setAiSessions(sessions);
        }
      }).catch(_temp2);
      const unsub = boardAgent.onSessionUpdate(data => {
        setAiSessions(prev => {
          const next = {
            ...prev
          };
          if (!data.phase) {
            delete next[data.postId];
          } else {
            next[data.postId] = data;
          }
          return next;
        });
      });
      return () => {
        ignore = true;
        unsub();
      };
    };
    t14 = [boardAgent];
    $[23] = boardAgent;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== aiSessions) {
    t15 = Object.values(aiSessions).filter(_temp3);
    $[26] = aiSessions;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const liveSessions = t15;
  let t16;
  if ($[28] !== liveSessions) {
    t16 = liveSessions.filter(_temp4);
    $[28] = liveSessions;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const activeSessions = t16;
  const activeCount = activeSessions.length;
  let t17;
  if ($[30] !== liveSessions) {
    t17 = liveSessions.filter(_temp5);
    $[30] = liveSessions;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const terminalCount = t17.length;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t18;
  if ($[32] !== searchParams) {
    t18 = searchParams?.get("card") ?? null;
    $[32] = searchParams;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const deepCard = t18;
  const bareDeepLinkId = deepCard && aiSessions[deepCard]?.mode === "bare" ? deepCard : null;
  const shownPostId = aiModal.postId ?? bareDeepLinkId;
  let t19;
  if ($[34] !== bareDeepLinkId || $[35] !== pathname || $[36] !== router) {
    t19 = () => {
      setAiModal({
        postId: null,
        forceBare: false
      });
      if (bareDeepLinkId && pathname) {
        router.replace(pathname);
      }
    };
    $[34] = bareDeepLinkId;
    $[35] = pathname;
    $[36] = router;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const closeSessionModal = t19;
  let t20;
  if ($[38] !== defaultsToBoard || $[39] !== pathname || $[40] !== router || $[41] !== searchParams) {
    t20 = board => {
      const params = new URLSearchParams(searchParams?.toString());
      if (board === defaultsToBoard) {
        params.delete("view");
      } else {
        params.set("view", board ? "board" : "space");
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname ?? "", {
        scroll: false
      });
    };
    $[38] = defaultsToBoard;
    $[39] = pathname;
    $[40] = router;
    $[41] = searchParams;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const setBoardView = t20;
  let t21;
  if ($[43] !== currentUser.isAuthenticated || $[44] !== isInvitedRole || $[45] !== joinSpaceUpdate || $[46] !== openAuthContinueModal || $[47] !== props || $[48] !== spaceSlug) {
    t21 = async () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      if (props.space.types.includes(Types.PAID)) {
        if (props.showSubscribeModalHandler) {
          props.showSubscribeModalHandler();
        }
      } else {
        if (!props.space.types.includes(Types.PRIVATE) || isInvitedRole) {
          const [error, data_0] = await client.spaces.join.mutate({
            slug: spaceSlug
          });
          if (error) {
            return;
          }
          joinSpaceUpdate(spaceSlug, data_0);
        }
      }
    };
    $[43] = currentUser.isAuthenticated;
    $[44] = isInvitedRole;
    $[45] = joinSpaceUpdate;
    $[46] = openAuthContinueModal;
    $[47] = props;
    $[48] = spaceSlug;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const joinHandler = t21;
  let t22;
  if ($[50] !== currentUser.isAuthenticated || $[51] !== isOwner || $[52] !== leaveSpaceUpdate || $[53] !== openAuthContinueModal || $[54] !== props.space || $[55] !== spaceSlug) {
    t22 = async () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      if (isOwner(props.space)) {
        return;
      }
      if (!props.space.types.includes(Types.PAID)) {
        const [error_0] = await client.spaces.leave.mutate({
          slug: spaceSlug
        });
        if (error_0) {
          return;
        }
        leaveSpaceUpdate(spaceSlug);
      } else {
        if (props.space.types.includes(Types.PAID)) {
          const conf = confirm("Your subscription will be cancelled and you'll not be able to post to this Space.");
          if (!conf) {
            return;
          }
          const [error_1] = await client.spaces.cancelSubscription.mutate({
            slug: spaceSlug
          });
          if (error_1) {
            return;
          }
          leaveSpaceUpdate(spaceSlug);
        }
      }
    };
    $[50] = currentUser.isAuthenticated;
    $[51] = isOwner;
    $[52] = leaveSpaceUpdate;
    $[53] = openAuthContinueModal;
    $[54] = props.space;
    $[55] = spaceSlug;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const leaveHandler = t22;
  let t23;
  if ($[57] !== spaceSlug) {
    t23 = async () => {
      await copyToClipboard(`${process.env.NEXT_PUBLIC_SITE_URL}/s/${spaceSlug}`);
      toast("Space link copied to clipboard");
      setProfileOptionsDropdownOpen(false);
    };
    $[57] = spaceSlug;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const copyLinkHandler = t23;
  let t24;
  if ($[59] !== queryClient || $[60] !== spaceSlug) {
    t24 = async type => {
      if (type === "add") {
        await client.spaces.addFilter.mutate({
          slug: spaceSlug
        });
      } else {
        if (type === "remove") {
          await client.spaces.removeFilter.mutate({
            slug: spaceSlug
          });
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ["users", "spaceFilters"]
      });
      setProfileOptionsDropdownOpen(false);
    };
    $[59] = queryClient;
    $[60] = spaceSlug;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const filterChangeHandler = t24;
  let t25;
  if ($[62] !== currentUser.isAuthenticated || $[63] !== newModerationReport || $[64] !== openAuthContinueModal || $[65] !== spaceSlug) {
    t25 = () => {
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      newModerationReport(ModerationReportType.SPACE, spaceSlug);
      setProfileOptionsDropdownOpen(false);
    };
    $[62] = currentUser.isAuthenticated;
    $[63] = newModerationReport;
    $[64] = openAuthContinueModal;
    $[65] = spaceSlug;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  const reportClickHandler = t25;
  const t26 = "flex";
  const t27 = `${props.hasBanner ? "" : "mt-2"} w-full`;
  const t28 = isBoardLayout ? <div style={props.hasBanner ? undefined : {
    marginTop: headerClearance
  }} className="relative z-20 mb-3 flex w-full flex-col items-stretch gap-2 rounded-xl border border-ds-border-subtle bg-background-secondary/60 px-3 py-2 shadow-sm backdrop-blur-xl sm:mb-4 sm:px-4 laptop:flex-row laptop:items-center laptop:gap-4"><div className="flex min-w-0 items-center gap-2.5"><Avatar spaceAvatarKey={props.space.avatarKey} size={36} className="shrink-0 rounded-lg" /><div className="flex min-w-0 flex-col leading-tight"><div className="flex items-center gap-1.5"><h1 className="truncate text-base font-bold text-textPrimary sm:text-lg">{props.space.name}</h1>{props.space.types.includes(Types.PRIVATE) && <span className="shrink-0 rounded border border-destructive/20 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-emphasis tracking-wide text-destructive">PRIVATE</span>}</div><button onClick={() => setShowMembersListModal(true)} className="mt-0.5 self-start text-xs text-textSecondary transition-colors hover:text-textPrimary sm:text-sm">{props.space.membersCount?.toLocaleString() ?? 0} members</button></div></div><div className="flex min-w-0 flex-wrap items-start gap-2 laptop:contents"><div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">{(() => {
          const p = activeRunner?.platform?.toLowerCase() ?? "";
          const localSelected = isElectron && !selectedRunner;
          const hasAny = isElectron || runners.length > 0;
          const Icon = localSelected ? Laptop : p === "darwin" ? Apple : p === "win32" ? MonitorCog : p === "linux" ? Server : activeRunner ? Laptop : Cpu;
          return <button data-id="board-runner" onClick={() => setRunnerSelectorOpen(true)} title={runnerSetup.phase === "error" ? humanizeSetupError(runnerSetup.error) : undefined} className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition-colors sm:text-sm ${runnerUnhealthy ? "bg-destructive/10 text-destructive hover:bg-destructive/20" : "text-textSecondary hover:bg-hover hover:text-textPrimary"}`}>{runnerUnhealthy ? <AlertCircle size={14} className="shrink-0" /> : <Icon size={14} />}<span className="max-w-[120px] truncate">{activeRunner?.name ?? (localSelected ? "Local" : hasAny ? "Select runner" : "Set up runner")}</span></button>;
        })()}{(isElectron || runners.length > 0) && activeCount > 0 && <div className="relative"><button data-id="board-active-runs" onClick={() => setAiSessionsOpen(_temp6)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 sm:text-sm"><Activity size={14} className="animate-pulse" /><span>{activeCount} Active</span></button>{aiSessionsOpen && <div className="absolute right-0 top-full z-50 mt-2 min-w-[260px] max-w-[min(400px,calc(100vw-1.5rem))] rounded-lg border border-white/10 bg-background-secondary p-2 shadow-xl"><p className="mb-2 px-2 text-xs font-semibold text-textSecondary">Active AI Sessions</p>{activeSessions.map(s_2 => <button key={s_2.postId} onClick={() => {
              setAiSessionsOpen(false);
              setAiModal({
                postId: s_2.postId,
                forceBare: false
              });
            }} className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-background-light"><span className="min-w-0 truncate text-textPrimary">{s_2.taskTitle || "Untitled"}</span><span className="ml-2 shrink-0 rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-medium capitalize text-primary">{s_2.phase}</span></button>)}</div>}</div>}{canToggleView && <button onClick={() => setBoardView(false)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-textSecondary transition-colors hover:bg-hover hover:text-textPrimary sm:text-sm"><List size={14} /><span className="hidden sm:inline">Space view</span></button>}<button data-id="board-ask-ai" onClick={() => setBoardAIOpen(true)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-textSecondary transition-colors hover:bg-hover hover:text-textPrimary sm:text-sm"><Sparkles size={14} /><span className="hidden sm:inline">Ask Board</span></button><button data-id="board-terminal" onClick={() => detached ? focusTerminalWindow({
          spaceSlug
        }) : setTerminalWindowOpen(true)} title={detached ? "Your terminal is open in its own window \u2014 bring it to the front" : terminalCount > 0 ? `${terminalCount} terminal session${terminalCount === 1 ? "" : "s"} running` : "Open a terminal session"} className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition-colors sm:text-sm ${terminalCount > 0 ? "text-primary hover:bg-primary/10" : "text-textSecondary hover:bg-hover hover:text-textPrimary"}`}>{detached ? <ExternalLink size={14} /> : <TerminalSquare size={14} className={terminalCount > 0 ? "animate-pulse" : ""} />}{terminalCount > 0 ? <span>{terminalCount}<span className="hidden sm:inline">{` Terminal${terminalCount === 1 ? "" : "s"}`}</span></span> : <span className="hidden sm:inline">Terminal</span>}</button><SkillsButton />{currentUser.isAuthenticated && <DeployButton />}{isOwner(props.space) && currentUser.isAuthenticated && <><button onClick={() => setFactiiiModalMode("lists")} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Tags size={14} /><span className="hidden sm:inline">List</span></button><button onClick={() => setTrelloImportModalOpen(true)} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Download size={14} /><span className="hidden sm:inline">Import</span></button></>}{props.onHistoryClick && <button data-id="board-activity" onClick={props.onHistoryClick} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary sm:text-sm"><Activity size={14} /><span className="hidden sm:inline">Activity</span></button>}</div><div className="flex shrink-0 items-center gap-1">{currentUser.isAuthenticated && <div className="relative" ref={dropdownRef}><button onClick={() => setProfileOptionsDropdownOpen(_temp7)} title="More options" className="flex h-8 items-center justify-center rounded-lg px-2 text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary"><MoreVertical size={14} /></button>{profileOptionsDropdownOpen && <div className="absolute right-0 top-full z-50 mt-2 min-w-[220px]"><SpaceDropdown space={props.space} uploads={props.uploads} copyLinkHandler={copyLinkHandler} slug={spaceSlug} loggedIn={currentUser.isAuthenticated} filtered={filtered} filterChangeHandler={filterChangeHandler} reportClickHandler={reportClickHandler} types={props.space.types} isInvited={isInvitedRole} joinHandler={joinHandler} leaveHandler={leaveHandler} closeHandler={() => setProfileOptionsDropdownOpen(false)} openEmbedModal={() => setEmbedSpaceModalOpen(true)} editHandler={() => setEditModalOpen(true)} showHistoryLink={!props.isHomePage} onTakeTour={spaceSlug === DEMO_SLUG ? undefined : () => setBoardTourOpen(true)} /></div>}</div>}{canJoin && !props.space.types.includes(Types.PAID) && <button onClick={joinHandler} className="flex h-8 items-center rounded-lg bg-primary px-3 text-xs font-semibold text-white transition-colors hover:bg-primary/90 sm:px-4 sm:text-sm">Join</button>}{canLeave && !props.space.types.includes(Types.PAID) && <button onClick={leaveHandler} className="flex h-8 items-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-red-700 sm:px-4 sm:text-sm">Leave</button>}{canLeave && spaceMembership.expires === null && props.space.types.includes(Types.PAID) && <button onClick={leaveHandler} className="flex h-8 items-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-red-700 sm:px-4 sm:text-sm">Cancel</button>}</div></div></div> : <PageBanner variant="space" topOffset={!props.hasBanner} entityKey={props.space.spaceSlug} bannerKey={props.space.bannerKey} bannerAlt={"Header Image for " + props.space.name} avatar={<Avatar key={`${props.space.spaceSlug}-${props.space.avatarKey ?? "default"}`} spaceAvatarKey={props.space.avatarKey} size={100} className="border-4 border-background-secondary" />} coverActions={!props.isEmbed && <>{canToggleView && <Button text="Board view" variant="CLEAR" classNames="px-2.5 py-1 text-xs" icon={<LayoutGrid size={15} />} onClick={() => setBoardView(true)} />}{isOwner(props.space) && currentUser.isAuthenticated && <><Button text="Edit Space" variant="CLEAR" classNames="px-2.5 py-1 text-xs" onClick={() => setEditModalOpen(true)} /><Button text="Manage Factiiis" variant="CLEAR" classNames="px-2.5 py-1 text-xs" onClick={() => setFactiiiModalMode("all")} /></>}{props.space.spaceSlug && <><BanStatsButton spaceSlug={props.space.spaceSlug} count={props.space.banned_count} /><RemovalStatsButton spaceSlug={props.space.spaceSlug} /></>}</>} title={props.space.name} subtitle={props.space.types.includes(Types.PRIVATE) && <BannerChip text="PRIVATE" tone="danger" />} description={props.space.description} stats={!props.isEmbed && !props.isHomePage && Boolean(props.space.membersCount) && <BannerStat label="Members" value={props.space.membersCount?.toLocaleString()} onClick={() => setShowMembersListModal(true)} />} actions={!props.isEmbed && currentUser.isAuthenticated !== null && spaceSlug !== "" && !props.isHomePage && <><BannerAction href={buildChatLink({
      spaceSlug
    })} icon={<MessageCircle size={15} />} text="Chat" /><BannerAction href={`/s/${spaceSlug}/history`} icon={<History size={15} />} text="History" />{canJoin && !props.space.types.includes(Types.PAID) && <Button text="Join Space" onClick={joinHandler} />}{canLeave && <Button text="Leave Space" variant="DANGER" disabled={isOwner(props.space)} onClick={leaveHandler} />}{canLeave && spaceMembership.expires === null && props.space.types.includes(Types.PAID) && <Button text="Cancel Membership" variant="DANGER" disabled={isOwner(props.space)} onClick={leaveHandler} />}{canLeave && spaceMembership.expires !== null && new Date(spaceMembership.expires ?? 0) >= new Date() && props.space.types.includes(Types.PAID) && <Button text={`Expires on ${new Date(spaceMembership.expires ?? 0).toLocaleDateString()}`} disabled={true} />}{currentUser.isAuthenticated && <BannerMenu label="Space Options Dropdown" menuRef={dropdownRef} open={profileOptionsDropdownOpen} onToggle={() => setProfileOptionsDropdownOpen(_temp8)}><div className="absolute right-0 top-full z-50 mt-2 min-w-[220px]"><SpaceDropdown space={props.space} uploads={props.uploads} copyLinkHandler={copyLinkHandler} slug={spaceSlug} loggedIn={currentUser.isAuthenticated} filtered={filtered} filterChangeHandler={filterChangeHandler} reportClickHandler={reportClickHandler} types={props.space.types} isInvited={isInvitedRole} joinHandler={joinHandler} leaveHandler={leaveHandler} closeHandler={() => setProfileOptionsDropdownOpen(false)} openEmbedModal={() => setEmbedSpaceModalOpen(true)} editHandler={() => setEditModalOpen(true)} showHistoryLink={!props.isHomePage} /></div></BannerMenu>}</>}>{!props.isEmbed && !notificationDismissed && !spaceMembership && isInvitedRole && <div className="mx-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm" role="alert"><p className="min-w-0 flex-1">You've been invited to join this invite-only space.{" "}<span onClick={joinHandler} className="cursor-pointer text-primary underline">Join now!</span></p><button type="button" aria-label="Close" onClick={() => setNotificationDismissed(true)} className="shrink-0 px-1 text-textSecondary hover:text-textPrimary"><span>×</span></button></div>}{props.space.pinnedFactiiis && props.space.pinnedFactiiis.length > 0 && <PinnedFactiiis spaceSlug={spaceSlug} factiiis={props.space.pinnedFactiiis} />}{props.space.headerBoards && props.space.headerBoards.length > 0 && <HeaderBoards boards={props.space.headerBoards} />}</PageBanner>;
  let t29;
  if ($[67] !== editModalOpen || $[68] !== props.space) {
    t29 = editModalOpen && <SpaceEdit closeHandler={() => setEditModalOpen(false)} pinnedFactiiis={props.space.pinnedFactiiis || []} space={props.space} />;
    $[67] = editModalOpen;
    $[68] = props.space;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== factiiiModalMode || $[71] !== isOwner || $[72] !== props.space) {
    t30 = factiiiModalMode && "spaceSlug" in props.space && <Modal closeHandler={() => setFactiiiModalMode(null)}><ModalHeader closeHandler={() => setFactiiiModalMode(null)}><h2>{factiiiModalMode === "lists" ? `Lists on ${props.space.spaceSlug}` : isOwner(props.space) ? `Factiiis for ${props.space.spaceSlug}` : "Factiiis for you on this space"}</h2></ModalHeader><ModalContent><FactiiisList space={props.space} slug={props.space.spaceSlug} onlyLists={factiiiModalMode === "lists"} /></ModalContent></Modal>;
    $[70] = factiiiModalMode;
    $[71] = isOwner;
    $[72] = props.space;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== isOwner || $[75] !== props.space || $[76] !== showMembersListModal || $[77] !== spaceSlug) {
    t31 = showMembersListModal && <Modal closeHandler={() => setShowMembersListModal(false)}><ModalHeader closeHandler={() => setShowMembersListModal(false)}><div className="flex flex-1 items-center justify-between"><h1>Space Members</h1>{isOwner(props.space) && <Button text="Invite User" classNames="text-sm mr-2" variant="SECONDARY" onClick={() => {
            setShowMembersListModal(false);
            setInviteMembersModalOpen(true);
          }} />}</div></ModalHeader><ModalContent><SpaceMembersList slug={spaceSlug} space={props.space} /></ModalContent></Modal>;
    $[74] = isOwner;
    $[75] = props.space;
    $[76] = showMembersListModal;
    $[77] = spaceSlug;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== inviteMembersModalOpen || $[80] !== spaceSlug) {
    t32 = inviteMembersModalOpen && <InviteMembersModal closeHandler={() => setInviteMembersModalOpen(false)} slug={spaceSlug} />;
    $[79] = inviteMembersModalOpen;
    $[80] = spaceSlug;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== embedSpaceModalOpen || $[83] !== spaceSlug) {
    t33 = embedSpaceModalOpen && <EmbedSpaceModal closeHandler={() => setEmbedSpaceModalOpen(false)} slug={spaceSlug} />;
    $[82] = embedSpaceModalOpen;
    $[83] = spaceSlug;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== spaceSlug || $[86] !== trelloImportModalOpen) {
    t34 = trelloImportModalOpen && <TrelloImportModal closeHandler={() => setTrelloImportModalOpen(false)} spaceSlug={spaceSlug} onImportComplete={_temp9} />;
    $[85] = spaceSlug;
    $[86] = trelloImportModalOpen;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== runnerSelectorOpen) {
    t35 = runnerSelectorOpen && <RunnerSelectorModal closeHandler={() => setRunnerSelectorOpen(false)} />;
    $[88] = runnerSelectorOpen;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== boardTourOpen) {
    t36 = boardTourOpen && <DevelopmentDemo onClose={() => setBoardTourOpen(false)} />;
    $[90] = boardTourOpen;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== boardAIOpen || $[93] !== queryClient || $[94] !== spaceSlug) {
    t37 = boardAIOpen && <BoardAIModal closeHandler={() => setBoardAIOpen(false)} factiiis={queryClient.getQueryData(["boardFactiiis", spaceSlug])?.pages.flatMap(_temp10) ?? []} spaceSlug={spaceSlug} onOpenTerminal={() => {
      setBoardAIOpen(false);
      setAiModal({
        postId: boardTerminalKey(spaceSlug),
        forceBare: true
      });
    }} />;
    $[92] = boardAIOpen;
    $[93] = queryClient;
    $[94] = spaceSlug;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== shownPostId || $[97] !== spaceSlug || $[98] !== terminalWindowOpen) {
    t38 = terminalWindowOpen && !shownPostId && <BareCardSessionModal spaceSlug={spaceSlug} closeHandler={() => setTerminalWindowOpen(false)} />;
    $[96] = shownPostId;
    $[97] = spaceSlug;
    $[98] = terminalWindowOpen;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== aiModal.forceBare || $[101] !== aiSessions || $[102] !== closeSessionModal || $[103] !== shownPostId || $[104] !== spaceSlug) {
    t39 = shownPostId && aiSessions[shownPostId] && (aiSessions[shownPostId].mode === "bare" || aiModal.forceBare ? <BareCardSessionModal post={{
      id: shownPostId,
      title: aiSessions[shownPostId].taskTitle || "",
      content: ""
    }} spaceSlug={spaceSlug} closeHandler={closeSessionModal} /> : <CardAISessionModal post={{
      id: shownPostId,
      title: aiSessions[shownPostId].taskTitle || "",
      content: "",
      contentRows: 1
    }} onOpenTerminal={() => setAiModal({
      postId: shownPostId,
      forceBare: true
    })} closeHandler={closeSessionModal} />);
    $[100] = aiModal.forceBare;
    $[101] = aiSessions;
    $[102] = closeSessionModal;
    $[103] = shownPostId;
    $[104] = spaceSlug;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== t27 || $[107] !== t28 || $[108] !== t29 || $[109] !== t30 || $[110] !== t31 || $[111] !== t32 || $[112] !== t33 || $[113] !== t34 || $[114] !== t35 || $[115] !== t36 || $[116] !== t37 || $[117] !== t38 || $[118] !== t39) {
    t40 = <div className={t26}><div className={t27}>{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}</div></div>;
    $[106] = t27;
    $[107] = t28;
    $[108] = t29;
    $[109] = t30;
    $[110] = t31;
    $[111] = t32;
    $[112] = t33;
    $[113] = t34;
    $[114] = t35;
    $[115] = t36;
    $[116] = t37;
    $[117] = t38;
    $[118] = t39;
    $[119] = t40;
  } else {
    t40 = $[119];
  }
  return t40;
};
export default Header;
async function _temp() {
  const [, d] = await client.users.spaceFilters.query();
  return d?.spaceIds ?? [];
}
function _temp2() {}
function _temp3(s) {
  return s.phase && s.phase !== "error";
}
function _temp4(s_0) {
  return s_0.mode !== "bare";
}
function _temp5(s_1) {
  return s_1.mode === "bare";
}
function _temp6(p_0) {
  return !p_0;
}
function _temp7(prev_0) {
  return !prev_0;
}
function _temp8(prev_1) {
  return !prev_1;
}
function _temp9() {
  window.location.reload();
}
function _temp10(p_1) {
  return p_1.factiiis;
}